import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { PODPhotoManager } from '../components/PODPhotoManager';
import { SignaturePad } from '../components/SignaturePad';
import ItemInspectionSection from '../components/ItemInspectionSection';
import {
  getPendingDeliveriesMobile,
  getDeliveryNoteMobile,
  quickDeliverMobile,
  markDNDelivered,
  uploadPODPhoto
} from '../lib/api';
import { enqueuePODUpload } from '../lib/offlineQueue';
import { getCachedPosition } from '../lib/geo';
import {
  Truck,
  Package,
  Users,
  FileText,
  MapPin,
  Phone,
  User,
  Calendar,
  Clock,
  CheckCircle,
  Camera,
  ChevronRight,
  ArrowLeft,
  RefreshCw,
  Navigation,
  AlertCircle,
  PenLine
} from 'lucide-react';

const typeIcons = {
  equipment: Package,
  manpower: Users,
  material: FileText,
  transportation: Truck
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  issued: 'bg-blue-100 text-blue-800',
  delivered: 'bg-green-100 text-green-800',
  acknowledged: 'bg-purple-100 text-purple-800'
};

const SIDE_TYPES = [
  { type: 'front', label: 'Front' },
  { type: 'back', label: 'Back' },
  { type: 'left', label: 'Left' },
  { type: 'right', label: 'Right' }
];

// Detect network / upload failure that should fall back to offline queue
const isOfflineError = (err) => {
  if (!navigator.onLine) return true;
  // Axios network error (no response) — server unreachable
  if (err && err.message && err.message === 'Network Error') return true;
  if (err && !err.response) return true;
  // 5xx server errors are also queued for retry
  if (err && err.response && err.response.status >= 500) return true;
  return false;
};

// Mandatory per-item 4-side photos for equipment/vehicle deliveries.
// Renders one section per DN item, then a single client-signature capture at
// the very end (a single signature applies to the entire delivery note).
const PerItemSidePhotos = ({ dn, item, index, onUploaded }) => {
  const [uploadingType, setUploadingType] = useState(null);
  const [offlineQueued, setOfflineQueued] = useState(new Set());
  const inputRefs = useRef({});
  const itemId = item.item_id;
  const label = item.description || item.name || `Item ${index + 1}`;

  const serverPhotoTypes = new Set(
    (dn.pod_photos || [])
      .filter((p) => p.item_id === itemId)
      .map((p) => p.photo_type)
  );
  const photoTypes = new Set([...serverPhotoTypes, ...offlineQueued]);
  const doneCount = SIDE_TYPES.filter(({ type }) => photoTypes.has(type)).length;

  const handleSideCapture = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!itemId) {
      toast.error('This item has no id yet — save the delivery note and reopen it, then capture photos.');
      return;
    }
    const gpsPromise = getCachedPosition().catch(() => null);
    try {
      setUploadingType(type);
      const gps = await gpsPromise;
      await uploadPODPhoto(dn.id, file, type, '', gps, itemId);
      toast.success(
        `${type.charAt(0).toUpperCase() + type.slice(1)} for ${label} uploaded${gps ? ' with GPS' : ''}`
      );
      await onUploaded();
    } catch (error) {
      if (isOfflineError(error)) {
        const gps = await gpsPromise;
        await enqueuePODUpload({
          dnId: dn.id,
          dnNumber: dn.dn_number,
          photoType: type,
          itemId,
          file,
          gps,
        });
        setOfflineQueued((s) => new Set([...s, type]));
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} for ${label} saved offline — will sync when online`);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to upload photo');
      }
    } finally {
      setUploadingType(null);
      e.target.value = '';
    }
  };

  return (
    <Card className="bg-white border-amber-300" data-testid={`item-photos-${itemId}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-slate-800">
              Item {index + 1}: <span className="text-slate-700">{label}</span>
            </h3>
            <p className="text-xs text-muted-foreground">Capture all 4 sides for this vehicle/equipment</p>
          </div>
          <Badge variant={doneCount === 4 ? 'default' : 'outline'} className={doneCount === 4 ? 'bg-green-600' : ''}>
            {doneCount}/4
          </Badge>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {SIDE_TYPES.map(({ type, label: sideLabel }) => {
            const done = photoTypes.has(type);
            return (
              <div key={type}>
                <input
                  ref={(el) => { inputRefs.current[type] = el; }}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleSideCapture(e, type)}
                />
                <Button
                  variant={done ? 'default' : 'outline'}
                  className={`w-full h-12 ${done ? 'bg-green-600 hover:bg-green-700' : 'border-dashed'}`}
                  onClick={() => inputRefs.current[type]?.click()}
                  disabled={uploadingType === type}
                  data-testid={`capture-${type}-${itemId}-btn`}
                >
                  {done ? <CheckCircle className="h-4 w-4 mr-2" /> : <Camera className="h-4 w-4 mr-2" />}
                  {uploadingType === type ? 'Uploading...' : `${sideLabel} Side`}
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Single client-signature capture that covers the whole delivery note
const ClientSignatureCapture = ({ dn, onUploaded }) => {
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [offlineDone, setOfflineDone] = useState(false);

  const hasSignature = offlineDone || (dn.pod_photos || []).some((p) => p.photo_type === 'signature');

  const handleSignatureSave = async (file) => {
    const gpsPromise = getCachedPosition().catch(() => null);
    try {
      setSaving(true);
      const gps = await gpsPromise;
      await uploadPODPhoto(dn.id, file, 'signature', '', gps);
      toast.success(`Client signature recorded${gps ? ' with GPS' : ''}`);
      setOpen(false);
      await onUploaded();
    } catch (error) {
      if (isOfflineError(error)) {
        const gps = await gpsPromise;
        await enqueuePODUpload({
          dnId: dn.id,
          dnNumber: dn.dn_number,
          photoType: 'signature',
          file,
          gps,
        });
        setOfflineDone(true);
        toast.success('Signature saved offline — will sync when online');
        setOpen(false);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save signature');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-white border-amber-300">
      <CardContent className="p-4">
        <h3 className="font-semibold text-slate-800 mb-1">Client Signature</h3>
        <p className="text-xs text-muted-foreground mb-3">
          One signature covers the entire delivery note and all items above.
        </p>
        <Button
          variant={hasSignature ? 'default' : 'outline'}
          className={`w-full h-12 ${hasSignature ? 'bg-green-600 hover:bg-green-700' : 'border-dashed'}`}
          onClick={() => setOpen(true)}
          data-testid="capture-signature-btn"
        >
          {hasSignature ? <CheckCircle className="h-4 w-4 mr-2" /> : <PenLine className="h-4 w-4 mr-2" />}
          {hasSignature ? 'Signature Recorded (tap to redo)' : 'Capture Client Signature'}
        </Button>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Client Signature - {dn.dn_number}</DialogTitle>
            </DialogHeader>
            <SignaturePad onSave={handleSignatureSave} saving={saving} />
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// List view component
const DeliveryList = ({ deliveries, onSelect, onRefresh, loading }) => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="flex items-center justify-between p-4">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Pending Deliveries</h1>
            <p className="text-sm text-muted-foreground">{deliveries.length} deliveries</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onRefresh} disabled={loading}>
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      {/* List */}
      <div className="p-4 space-y-3">
        {deliveries.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="p-8 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <p className="text-lg font-medium text-slate-700">All caught up!</p>
              <p className="text-sm text-muted-foreground mt-1">No pending deliveries</p>
            </CardContent>
          </Card>
        ) : (
          deliveries.map((dn) => {
            const TypeIcon = typeIcons[dn.dn_type] || Truck;
            return (
              <Card 
                key={dn.id}
                className="bg-white hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => onSelect(dn)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-slate-100 rounded-lg">
                        <TypeIcon className="h-5 w-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{dn.dn_number}</p>
                        <p className="text-sm text-slate-600">{dn.client_name}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>{dn.delivery_date}</span>
                          {dn.photo_count > 0 && (
                            <>
                              <Camera className="h-3 w-3 ml-2" />
                              <span>{dn.photo_count}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className={statusColors[dn.status]}>{dn.status}</Badge>
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    </div>
                  </div>
                  {dn.delivery_location && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t text-sm text-slate-600">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      <span className="truncate">{dn.delivery_location}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

// Detail view component
const DeliveryDetail = ({ dnId, onBack }) => {
  const [dn, setDn] = useState(null);
  const [inspComplete, setInspComplete] = useState({});

  // Stable callbacks for per-item children — guarded so the setter bails
  // when the value hasn't actually changed (prevents render loops).
  const handleInspValidityChange = useCallback((itemId, ok) => {
    setInspComplete((p) => (p[itemId] === ok ? p : { ...p, [itemId]: ok }));
  }, []);
  const handleInspectionChange = useCallback((itemId, next) => {
    setDn((prev) => {
      if (!prev) return prev;
      const items = (prev.items || []).map((x) =>
        (x.item_id || '') === (itemId || '')
          ? { ...x, inspection: next }
          : x
      );
      return { ...prev, items };
    });
  }, []);
  const [loading, setLoading] = useState(true);
  const [delivering, setDelivering] = useState(false);
  const [deliverDialogOpen, setDeliverDialogOpen] = useState(false);
  const [deliverForm, setDeliverForm] = useState({
    receiver_name: '',
    receiver_contact: '',
    notes: ''
  });
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);

  // Fetch delivery note
  const fetchDN = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getDeliveryNoteMobile(dnId);
      setDn(response.data);
    } catch (error) {
      toast.error('Failed to load delivery note');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [dnId]);

  useEffect(() => {
    fetchDN();
  }, [fetchDN]);

  // Get GPS location
  const getGPSLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setGettingLocation(false);
        toast.success('Location captured');
      },
      (error) => {
        console.error('GPS error:', error);
        toast.error('Failed to get location');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Mark as delivered
  const handleDeliver = async () => {
    try {
      setDelivering(true);
      const data = {
        ...deliverForm,
        ...(gpsLocation || {})
      };
      await quickDeliverMobile(dnId, data);
      toast.success('Delivery completed!');
      setDeliverDialogOpen(false);
      await fetchDN();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as delivered');
      console.error(error);
    } finally {
      setDelivering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!dn) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
          <p className="text-slate-600">Delivery note not found</p>
          <Button variant="link" onClick={onBack}>Go Back</Button>
        </div>
      </div>
    );
  }

  const TypeIcon = typeIcons[dn.dn_type] || Truck;
  const isDelivered = dn.status === 'delivered' || dn.status === 'acknowledged';
  
  // Mandatory POD requirements for equipment/vehicle deliveries
  const requiresSidePhotos = ['equipment', 'transportation'].includes(dn.dn_type);
  const photoTypes = new Set((dn.pod_photos || []).map(p => p.photo_type));
  const itemsWithIds = (dn.items || []).filter((it) => it && it.item_id);
  const missingRequired = requiresSidePhotos
    ? [
        ...(itemsWithIds.length
          ? itemsWithIds.flatMap((it) => {
              const itemTypes = new Set(
                (dn.pod_photos || [])
                  .filter((p) => p.item_id === it.item_id)
                  .map((p) => p.photo_type)
              );
              const label = it.description || it.name || it.item_id;
              return ['front', 'back', 'left', 'right']
                .filter((t) => !itemTypes.has(t))
                .map((t) => `${t} · ${label}`);
            })
          : ['front', 'back', 'left', 'right'].filter((t) => !photoTypes.has(t))
        ),
        ...(photoTypes.has('signature') ? [] : ['signature'])
      ]
    : [];

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="flex items-center gap-3 p-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-800">{dn.dn_number}</h1>
            <div className="flex items-center gap-2">
              <Badge className={statusColors[dn.status]}>{dn.status}</Badge>
              <span className="text-xs text-muted-foreground capitalize">{dn.dn_type}</span>
            </div>
          </div>
          <TypeIcon className="h-6 w-6 text-slate-400" />
        </div>
      </header>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Client Info */}
        <Card className="bg-white">
          <CardContent className="p-4">
            <h3 className="font-semibold text-slate-800 mb-3">Delivery Details</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Client</p>
                  <p className="font-medium">{dn.client_name}</p>
                </div>
              </div>
              {dn.delivery_location && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">{dn.delivery_location}</p>
                  </div>
                </div>
              )}
              {dn.contact_person && (
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Contact Person</p>
                    <p className="font-medium">{dn.contact_person}</p>
                  </div>
                </div>
              )}
              {dn.contact_phone && (
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <a href={`tel:${dn.contact_phone}`} className="font-medium text-blue-600">
                      {dn.contact_phone}
                    </a>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Delivery Date</p>
                  <p className="font-medium">{dn.delivery_date}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Items */}
        <Card className="bg-white">
          <CardContent className="p-4">
            <h3 className="font-semibold text-slate-800 mb-3">Items ({dn.items?.length || 0})</h3>
            <div className="space-y-2">
              {dn.items?.map((item, index) => (
                <div key={item.item_id || index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm">{item.description}</span>
                  <Badge variant="outline">{item.quantity} {item.unit}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Per-item Inspection Checklists (mandatory before Finalize) */}
        {!isDelivered && (dn.items || []).some((it) => it.asset_id) && (
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <h3 className="text-sm font-semibold text-slate-700">Inspection Checklists</h3>
              <Badge
                variant="outline"
                className={
                  (dn.items || []).filter((i) => i.asset_id && inspComplete[i.item_id]).length ===
                  (dn.items || []).filter((i) => i.asset_id).length
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                    : 'bg-amber-50 text-amber-800 border-amber-300'
                }
              >
                {(dn.items || []).filter((i) => i.asset_id && inspComplete[i.item_id]).length}
                /{(dn.items || []).filter((i) => i.asset_id).length}
              </Badge>
            </div>
            {(dn.items || [])
              .filter((it) => it.asset_id)
              .map((it, idx) => (
                <ItemInspectionSection
                  key={it.item_id || idx}
                  dnId={dn.id}
                  item={it}
                  index={idx}
                  onInspectionChange={(next) => handleInspectionChange(it.item_id, next)}
                  onValidityChange={(ok) => handleInspValidityChange(it.item_id, ok)}
                />
              ))}
          </div>
        )}

        {/* Per-item 4-side photos + single signature (equipment/vehicles) */}
        {!isDelivered && requiresSidePhotos && (
          <>
            {(dn.items || []).map((item, index) => (
              <PerItemSidePhotos
                key={item.item_id || index}
                dn={dn}
                item={item}
                index={index}
                onUploaded={fetchDN}
              />
            ))}
            <ClientSignatureCapture dn={dn} onUploaded={fetchDN} />
          </>
        )}

        {/* POD Photos */}
        <PODPhotoManager
          dnId={dn.id}
          dnNumber={dn.dn_number}
          initialPhotos={dn.pod_photos || []}
          readOnly={isDelivered}
        />

        {/* Delivery Info (if delivered) */}
        {isDelivered && dn.delivered_at && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <h3 className="font-semibold text-green-800">Delivered</h3>
              </div>
              <div className="space-y-2 text-sm">
                <p><span className="text-muted-foreground">By:</span> {dn.delivered_by_name || 'Unknown'}</p>
                <p><span className="text-muted-foreground">At:</span> {new Date(dn.delivered_at).toLocaleString()}</p>
                {dn.receiver_name && (
                  <p><span className="text-muted-foreground">Receiver:</span> {dn.receiver_name}</p>
                )}
                {dn.delivery_location_gps && (
                  <p className="flex items-center gap-1">
                    <Navigation className="h-3 w-3" />
                    <span className="text-muted-foreground">GPS:</span> 
                    {dn.delivery_location_gps.latitude.toFixed(6)}, {dn.delivery_location_gps.longitude.toFixed(6)}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bottom Action Bar */}
      {!isDelivered && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 shadow-lg">
          {(() => {
            const inspecItems = (dn.items || []).filter((i) => i.asset_id);
            const inspecIncomplete = inspecItems.filter((i) => !inspComplete[i.item_id]).length;
            const disabled = missingRequired.length > 0 || inspecIncomplete > 0;
            return (
              <>
                {missingRequired.length > 0 && (
                  <p className="text-xs text-red-600 mb-1 text-center" data-testid="missing-photos-warning">
                    Photos required: {missingRequired.join(', ')}
                  </p>
                )}
                {inspecIncomplete > 0 && (
                  <p className="text-xs text-amber-700 mb-2 text-center" data-testid="missing-inspection-warning">
                    {inspecIncomplete} item inspection(s) still incomplete
                  </p>
                )}
                <Button
                  className="w-full h-12 text-lg bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    if (disabled) {
                      toast.error('All fields and photos are required for compliance. Complete every inspection before finalizing.');
                      return;
                    }
                    setDeliverDialogOpen(true);
                  }}
                  disabled={disabled}
                  data-testid="mark-delivered-btn"
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Mark as Delivered
                </Button>
              </>
            );
          })()}
        </div>
      )}

      {/* Deliver Dialog */}
      <Dialog open={deliverDialogOpen} onOpenChange={setDeliverDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Delivery</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Receiver Name</Label>
              <Input
                value={deliverForm.receiver_name}
                onChange={(e) => setDeliverForm({...deliverForm, receiver_name: e.target.value})}
                placeholder="Name of person receiving delivery"
              />
            </div>
            <div>
              <Label>Receiver Contact</Label>
              <Input
                value={deliverForm.receiver_contact}
                onChange={(e) => setDeliverForm({...deliverForm, receiver_contact: e.target.value})}
                placeholder="Phone number"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={deliverForm.notes}
                onChange={(e) => setDeliverForm({...deliverForm, notes: e.target.value})}
                placeholder="Any delivery notes..."
                rows={2}
              />
            </div>
            <div>
              <Label>GPS Location</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button
                  variant="outline"
                  onClick={getGPSLocation}
                  disabled={gettingLocation}
                  className="flex-1"
                >
                  <Navigation className={`h-4 w-4 mr-2 ${gettingLocation ? 'animate-pulse' : ''}`} />
                  {gettingLocation ? 'Getting Location...' : 'Capture Location'}
                </Button>
                {gpsLocation && (
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Captured
                  </Badge>
                )}
              </div>
              {gpsLocation && (
                <p className="text-xs text-muted-foreground mt-1">
                  {gpsLocation.latitude.toFixed(6)}, {gpsLocation.longitude.toFixed(6)}
                </p>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setDeliverDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleDeliver}
                disabled={delivering}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {delivering ? 'Processing...' : 'Confirm Delivery'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Page Component
export const MobileDeliveryPage = () => {
  const navigate = useNavigate();
  const { dnId } = useParams();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDN, setSelectedDN] = useState(null);

  // Fetch pending deliveries
  const fetchDeliveries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPendingDeliveriesMobile();
      setDeliveries(response.data);
    } catch (error) {
      toast.error('Failed to load deliveries');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!dnId) {
      fetchDeliveries();
    }
  }, [fetchDeliveries, dnId]);

  // If we have a dnId in URL, show detail view
  if (dnId) {
    return (
      <DeliveryDetail
        dnId={dnId}
        onBack={() => navigate('/mobile/delivery')}
      />
    );
  }

  // If we selected a DN, show detail view
  if (selectedDN) {
    return (
      <DeliveryDetail
        dnId={selectedDN.id}
        onBack={() => {
          setSelectedDN(null);
          fetchDeliveries();
        }}
      />
    );
  }

  // Otherwise show list
  return (
    <DeliveryList
      deliveries={deliveries}
      onSelect={setSelectedDN}
      onRefresh={fetchDeliveries}
      loading={loading}
    />
  );
};

export default MobileDeliveryPage;
